import React, { useEffect, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack,
  Switch, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import { Add, Delete, Save, FileUpload, FileDownload, Download } from '@mui/icons-material';
import api from '../../services/api';
import TextField from '../common/TextField';

interface Category { id: number; name: string; isActive: boolean; sortOrder: number; }

interface Props {
  open: boolean;
  onClose: () => void;
  /** Llamado cuando cambia algo, para que el padre refresque su lista. */
  onChanged?: () => void;
}

const TEMPLATE_CSV = 'name,sortOrder,isActive\n"Alimentos",0,true\n"Bebidas",1,true\n';

export const CategoriesManagerModal: React.FC<Props> = ({ open, onClose, onChanged }) => {
  const [items, setItems] = useState<Category[]>([]);
  const [newName, setNewName] = useState('');
  const [loading, setLoading] = useState(false);
  const [importSummary, setImportSummary] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.getCategories();
      setItems((res.items || []).sort((a, b) => (a.sortOrder - b.sortOrder) || a.name.localeCompare(b.name)));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) { load(); setImportSummary(null); } /* eslint-disable-next-line */ }, [open]);

  const notify = () => { onChanged?.(); };

  const create = async () => {
    const name = newName.trim();
    if (!name) return;
    await api.createCategory({ name, isActive: true, sortOrder: items.length });
    setNewName('');
    await load();
    notify();
  };

  const save = async (c: Category) => {
    await api.updateCategory(c.id, { name: c.name.trim(), isActive: c.isActive, sortOrder: c.sortOrder });
    await load();
    notify();
  };

  const remove = async (c: Category) => {
    await api.deleteCategory(c.id);
    await load();
    notify();
  };

  const patch = (id: number, p: Partial<Category>) =>
    setItems(prev => prev.map(it => (it.id === id ? { ...it, ...p } : it)));

  const exportCsv = async () => {
    const blob = await api.downloadCategoriesCsv();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'categorias.csv'; a.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'plantilla-categorias.csv'; a.click();
    window.URL.revokeObjectURL(url);
  };

  const parseCsv = (text: string) => {
    const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
    if (lines.length === 0) return [] as { name: string; sortOrder?: number; isActive?: boolean }[];
    const header = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, '').toLowerCase());
    const idx = (n: string) => header.indexOf(n);
    const rows: { name: string; sortOrder?: number; isActive?: boolean }[] = [];
    for (const line of lines.slice(1)) {
      const cols = line.match(/\s*([^",]+|"[^"]*")(?:,|$)/g)?.map(x => x.replace(/,$/, '').trim()) || line.split(',');
      const val = (i: number) => (i < 0 || i >= cols.length ? '' : (cols[i]?.replace(/^"|"$/g, '') || ''));
      const name = val(idx('name')) || val(idx('nombre'));
      if (!name) continue;
      rows.push({
        name,
        sortOrder: Number(val(idx('sortorder')) || 0),
        isActive: (val(idx('isactive')) || 'true').toLowerCase() !== 'false',
      });
    }
    return rows;
  };

  const onImportFile = async (file: File) => {
    const text = await file.text();
    const rows = parseCsv(text);
    if (rows.length === 0) { setImportSummary('No se detectaron filas válidas.'); return; }
    const res = await api.bulkImportCategories(rows);
    setImportSummary(`Creadas: ${res.created} • Actualizadas: ${res.updated} • Omitidas: ${res.skipped}`);
    await load();
    notify();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Gestionar Categorías</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={1}>
            <TextField size="small" fullWidth label="Nueva categoría" value={newName}
              onChange={e => setNewName(e.target.value)} onKeyDown={e => e.key === 'Enter' && create()} />
            <Button variant="contained" startIcon={<Add />} onClick={create}>Crear</Button>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button size="small" variant="outlined" startIcon={<FileDownload />} onClick={exportCsv}>Exportar CSV</Button>
            <Button size="small" variant="outlined" component="label" startIcon={<FileUpload />}>
              Importar CSV
              <input hidden type="file" accept=".csv,text/csv" onChange={e => e.target.files?.[0] && onImportFile(e.target.files[0])} />
            </Button>
            <Button size="small" variant="text" startIcon={<Download />} onClick={downloadTemplate}>Descargar plantilla</Button>
          </Stack>
          {importSummary && <Typography variant="caption" color="text.secondary">{importSummary}</Typography>}

          {items.length === 0 ? (
            <Typography variant="body2" color="text.secondary">{loading ? 'Cargando…' : 'No hay categorías creadas'}</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nombre</TableCell>
                  <TableCell align="center">Orden</TableCell>
                  <TableCell align="center">Activa</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map(c => (
                  <TableRow key={c.id}>
                    <TableCell>
                      <TextField size="small" value={c.name} onChange={e => patch(c.id, { name: e.target.value })} />
                    </TableCell>
                    <TableCell align="center">
                      <TextField size="small" type="number" sx={{ width: 80 }} value={c.sortOrder}
                        onChange={e => patch(c.id, { sortOrder: Number(e.target.value) })} />
                    </TableCell>
                    <TableCell align="center">
                      <Switch size="small" checked={c.isActive} onChange={e => patch(c.id, { isActive: e.target.checked })} />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" color="primary" onClick={() => save(c)}><Save fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => remove(c)}><Delete fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CategoriesManagerModal;
